import {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import AppsIcon from "@mui/icons-material/Apps";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {Button, Divider, Drawer, Grid, IconButton, Stack, Tooltip, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {getCustomPageInternalUrl} from "../../constants/appRoutes";
import {NodeVisibility} from "../../model/IHubMinimalNode.d.ts";
import {ConfigNodeMenuId} from "../../model/item-containers-models/configNodeMenuId";
import {I18N_ADORNMENT_VIEW_MODE_TEXT} from "../i18n-input-adornment-select";
import ModulesPlaceholder from "../modules-placeholder";
import A11ySelectButton from "./components/A11ySelectButton";
import AppLanguageSelectButton from "./components/AppLanguageSelectButton";
import DashboardButton from "./components/DashboardButton";
import HomeButton from "./components/HomeButton";
import HubLogo from "./components/HubLogo";
import InfoButton from "./components/InfoButton";
import NavigationDrawerButtons from "./components/NavigationDrawerButtons";
import NodeSelector from "./components/NodeSelector";
import SearchButton from "./components/SearchButton";
import SettingsSelectButton from "./components/SettingsSelectButton";
import ToolsSelectButton from "./components/ToolsSelectButton";
import UserItemsSelectButton from "./components/UserItemsSettingsButton";
import UserSelectButton from "./components/UserSelectButton";
import {nodeCustomPagesSelector} from "../../state/node/nodeSelectors";
import {WINDOW_SCREEN_WIDTH_MD} from "../../utils/constants";
import {localizeI18nObj} from "../../utils/i18n";
import {getCustomPagesInMenu} from "../../utils/itemContainers";
import {HEADER_BUTTON_VIEW_MODE_ICON, HEADER_BUTTON_VIEW_MODE_TEXT} from "./utils";

const $ = window.jQuery;

const HeaderVariantInline = ({
  baseURL,
  isA11y,
  defaultLanguage,
  languages,
  moduleConfigs,
  hub,
  nodes,
  node,
  title,
  noNode,
  catalog,
  user,
  filteredCatalog,
  isDefaultAndUniqueNode,
  selectedCategoryPath,
  selectedDataset,
  onRender
}) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [isFirstRowCollapsed, setisFirstRowCollapsed] = useState(false);
  const [isToolbarDrawerOpen, setisToolbarDrawerOpen] = useState(false);

  const nodeCustomPages = useSelector(nodeCustomPagesSelector);
  const customPagesInCentralMenu = getCustomPagesInMenu(nodeCustomPages, ConfigNodeMenuId.CENTRAL);

  const headerLogoSmallURL = useMemo(() => hub.extras.HeaderLogoSmallURL || "", [hub.extras]);
  const headerLogoAlt = useMemo(
    () => localizeI18nObj(hub.extras.HeaderLogoAlt, defaultLanguage, languages) || "",
    [hub.extras, defaultLanguage, languages]
  );
  const headerLogoHref = useMemo(() => hub.extras.HeaderLogoHref || "", [hub.extras]);
  const headerLogoTitle = useMemo(
    () => localizeI18nObj(hub.extras.headerLogoTitle, defaultLanguage, languages) || "",
    [hub.extras, defaultLanguage, languages]
  );

  const onToolbarDrawerOpen = useCallback(() => {
    setisToolbarDrawerOpen(true);
  }, []);

  const onToolbarDrawerClose = useCallback(() => {
    setisToolbarDrawerOpen(false);
  }, []);

  const handleHeaderStyle = useCallback(() => {
    setisFirstRowCollapsed(window.innerWidth < WINDOW_SCREEN_WIDTH_MD);

    const leftContainerWidth = $("#header__first-row__left-container").outerWidth(true) || 0;
    const rightContainerRestWidth = $("#header__first-row__right-container__rest").outerWidth(true) || 0;
    $("#header__first-row__right-container__node-selector .node-selector").css({
      maxWidth: `calc(100vw - ${leftContainerWidth + rightContainerRestWidth + 40}px)`
    });

    if (onRender) {
      setTimeout(() => onRender(), 0);
    }
  }, [onRender]);

  useEffect(() => {
    window.addEventListener("resize", handleHeaderStyle);
    return () => {
      window.removeEventListener("resize", handleHeaderStyle);
    };
  }, [handleHeaderStyle]);

  useEffect(() => {
    handleHeaderStyle();
  });

  return (
    <>
      <Grid
        container
        justifyContent="space-between"
        wrap={"nowrap"}
        sx={{
          backgroundColor: "white",
          color: theme.palette.primary.main,
          minHeight: "60px"
        }}
        id="header__first-row"
      >
        <Grid display={"flex"} alignItems={"center"} id={"header__first-row__left-container"}>
          <Grid
            container
            sx={{padding: "4px 8px"}}
            columnSpacing={1}
            wrap={"nowrap"}
            display={"flex"}
            alignItems={"center"}
          >
            {!noNode && node?.visible !== NodeVisibility.No && catalog && !catalog.isEmpty && (
              <NavigationDrawerButtons
                selectedCategoryPath={selectedCategoryPath}
                selectedDataset={selectedDataset}
                title={title}
                nodes={nodes}
                catalog={catalog}
                node={node}
                hub={hub}
                filteredCatalog={filteredCatalog}
                defaultLanguage={defaultLanguage}
                languages={languages}
                moduleConfigs={moduleConfigs}
                DrawerIcon={AppsIcon}
                drawerButtonProps={{
                  color: "primary",
                  sx: {
                    color: "white",
                    backgroundColor: theme.palette.primary.main,
                    "&:hover": {backgroundColor: theme.palette.primary.dark}
                  }
                }}
              />
            )}
            <HubLogo
              hub={hub}
              headerLogoTitle={headerLogoTitle}
              headerLogoHref={headerLogoHref}
              baseURL={baseURL}
              headerLogoAlt={headerLogoAlt}
              headerLogoSmallURL={headerLogoSmallURL}
              sx={{
                "& a > span": {
                  padding: "2px"
                },
                "& a > span > img": {
                  height: "48px"
                }
              }}
            />
            {!isFirstRowCollapsed ? (
              <Fragment>
                <HomeButton noNode={noNode} node={node} />
                <DashboardButton
                  viewMode={HEADER_BUTTON_VIEW_MODE_TEXT}
                  moduleConfigs={moduleConfigs}
                  node={node}
                  hub={hub}
                />
                <Grid>
                  <ModulesPlaceholder id="header-variant-inline" />
                </Grid>
              </Fragment>
            ) : null}
          </Grid>
        </Grid>
        <Grid display={"flex"} alignItems={"center"} id={"header__first-row__right-container"}>
          <Grid
            container
            sx={{padding: "4px 8px"}}
            columnSpacing={1}
            wrap={"nowrap"}
            display={"flex"}
            alignItems={"center"}
          >
            <Grid container id={"header__first-row__right-container__node-selector"}>
              {!noNode && (
                <NodeSelector
                  isDefaultUniqueNode={isDefaultAndUniqueNode}
                  node={node}
                  nodes={nodes}
                  fontSize={"16px"}
                />
              )}
            </Grid>
            <Grid
              container
              columnSpacing={1}
              wrap={"nowrap"}
              display={"flex"}
              alignItems={"center"}
              id={"header__first-row__right-container__rest"}
            >
              {!isFirstRowCollapsed ? (
                <Fragment>
                  <SearchButton noNode={noNode} catalog={catalog} />
                  <AppLanguageSelectButton viewMode={I18N_ADORNMENT_VIEW_MODE_TEXT} />
                  <A11ySelectButton viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
                  <SettingsSelectButton user={user} nodes={nodes} viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
                  <UserSelectButton
                    hub={hub}
                    nodes={nodes}
                    user={user}
                    disabled={isFirstRowCollapsed}
                    viewMode={HEADER_BUTTON_VIEW_MODE_ICON}
                  />
                  <UserItemsSelectButton
                    hub={hub}
                    nodes={nodes}
                    user={user}
                    disabled={isFirstRowCollapsed}
                    viewMode={HEADER_BUTTON_VIEW_MODE_ICON}
                  />
                  <ToolsSelectButton node={node} user={user} viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
                  <InfoButton hub={hub} />
                </Fragment>
              ) : (
                <Grid className="toolbar-drawer-btn">
                  <Tooltip title={t("components.header.actions.openToolbarDrawer.title")}>
                    <IconButton
                      color="inherit"
                      onClick={onToolbarDrawerOpen}
                      aria-label={t("components.header.actions.openToolbarDrawer.ariaLabel")}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {!isA11y && moduleConfigs.modules.includes("custom-page") && customPagesInCentralMenu.length > 0 && (
        <Grid
          id="header__second-row"
          container
          columnSpacing={1}
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: "4px"
          }}
        >
          {customPagesInCentralMenu.map(v => (
            <Grid key={v.id}>
              <Button
                sx={{textTransform: "none"}}
                color="inherit"
                variant="text"
                onClick={() => navigate(getCustomPageInternalUrl(v.id, node?.code))}
              >
                {localizeI18nObj(v.title, defaultLanguage, languages)}
              </Button>
            </Grid>
          ))}
        </Grid>
      )}
      <Drawer
        anchor="right"
        open={isToolbarDrawerOpen}
        PaperProps={{
          sx: {
            overflow: "hidden",
            padding: "8px"
          }
        }}
        onClose={onToolbarDrawerClose}
      >
        <nav id="toolbar-drawer" aria-label={t("components.header.toolbarDrawer.ariaLabel")}>
          <Stack spacing={1} alignItems="center">
            <Grid>
              <Tooltip title={t("components.header.actions.closeToolbarDrawer.title")}>
                <IconButton
                  color="inherit"
                  onClick={onToolbarDrawerClose}
                  aria-label={t("components.header.actions.closeToolbarDrawer.ariaLabel")}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid
              sx={{
                width: "100%",
                margin: "4px 0"
              }}
            >
              <Divider />
            </Grid>
            <InfoButton hub={hub} />
            <SearchButton noNode={noNode} catalog={catalog} />
            <AppLanguageSelectButton viewMode={I18N_ADORNMENT_VIEW_MODE_TEXT} />
            <A11ySelectButton viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
            <SettingsSelectButton user={user} nodes={nodes} viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
            <UserSelectButton
              hub={hub}
              nodes={nodes}
              user={user}
              disabled={isFirstRowCollapsed}
              viewMode={HEADER_BUTTON_VIEW_MODE_ICON}
            />
            <UserItemsSelectButton
              hub={hub}
              nodes={nodes}
              user={user}
              disabled={isFirstRowCollapsed}
              viewMode={HEADER_BUTTON_VIEW_MODE_ICON}
            />
            <ToolsSelectButton node={node} user={user} viewMode={HEADER_BUTTON_VIEW_MODE_ICON} />
            <HomeButton noNode={noNode} node={node} />
            <DashboardButton
              viewMode={HEADER_BUTTON_VIEW_MODE_TEXT}
              moduleConfigs={moduleConfigs}
              node={node}
              hub={hub}
            />
            <Grid>
              <ModulesPlaceholder id="header-variant-inline" />
            </Grid>
          </Stack>
        </nav>
      </Drawer>
    </>
  );
};

export default HeaderVariantInline;
